/**
 * Security utilities for API protection and encryption
 */

// Generate a unique browser fingerprint
export const generateFingerprint = async () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.fillText('Browser Fingerprint', 2, 15);
  
  const canvasData = canvas.toDataURL();
  
  const fingerprint = {
    canvas: await hashString(canvasData),
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: Date.now()
  };
  
  return fingerprint;
};

// Hash a string using SubtleCrypto
export const hashString = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Generate a request signature
export const generateRequestSignature = async (data) => {
  const timestamp = Date.now();
  const nonce = Math.random().toString(36).substring(2);
  const payload = JSON.stringify({ ...data, timestamp, nonce });
  const signature = await hashString(payload);
  
  return {
    timestamp,
    nonce,
    signature
  };
};

// Simple XOR encryption for API URLs (obfuscation layer)
export const obfuscateString = (str) => {
  const key = 'security-key-2024';
  let result = '';
  for (let i = 0; i < str.length; i++) {
    result += String.fromCharCode(str.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(result); // Base64 encode
};

export const deobfuscateString = (str) => {
  const key = 'security-key-2024';
  const decoded = atob(str);
  let result = '';
  for (let i = 0; i < decoded.length; i++) {
    result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
};

// Honeypot field detection
export const createHoneypot = () => {
  return {
    fieldName: `field_${Math.random().toString(36).substring(2)}`,
    expectedValue: ''
  };
};

// Rate limiting check (client-side)
const requestTimestamps = [];
export const checkRateLimit = (maxRequests = 5, timeWindow = 60000) => {
  const now = Date.now();
  const recentRequests = requestTimestamps.filter(t => now - t < timeWindow);
  
  if (recentRequests.length >= maxRequests) {
    return false; // Rate limit exceeded
  }
  
  requestTimestamps.push(now);
  // Keep only recent timestamps
  while (requestTimestamps.length > 0 && now - requestTimestamps[0] > timeWindow) {
    requestTimestamps.shift();
  }
  
  return true;
};

// Detect bot-like behavior
export const detectBotBehavior = () => {
  const indicators = {
    webdriver: navigator.webdriver,
    phantom: window.callPhantom || window._phantom,
    nightmare: window.__nightmare,
    selenium: document.documentElement.getAttribute('selenium') !== null,
    webdriverIE: document.documentElement.getAttribute('webdriver') !== null,
    chromeHeadless: /HeadlessChrome/.test(navigator.userAgent),
    plugins: navigator.plugins.length === 0,
    automation: navigator.automation || false
  };
  
  const suspicionScore = Object.values(indicators).filter(Boolean).length;
  return {
    isSuspicious: suspicionScore > 0,
    score: suspicionScore,
    indicators
  };
};

// Generate CSRF-like token
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Store token in session
export const setSecurityToken = (token) => {
  sessionStorage.setItem('sec_token', token);
  sessionStorage.setItem('sec_token_ts', Date.now().toString());
};

export const getSecurityToken = () => {
  const token = sessionStorage.getItem('sec_token');
  const timestamp = parseInt(sessionStorage.getItem('sec_token_ts') || '0');
  
  // Token expires after 30 minutes
  if (Date.now() - timestamp > 30 * 60 * 1000) {
    return null;
  }
  
  return token;
};

// Enhanced fetch with security
export const secureFetch = async (url, options = {}) => {
  // Check rate limit
  if (!checkRateLimit()) {
    throw new Error('Rate limit exceeded. Please wait before trying again.');
  }
  
  // Detect bot behavior
  const botCheck = detectBotBehavior();
  if (botCheck.isSuspicious) {
    console.warn('Suspicious bot behavior detected');
  }
  
  // Generate fingerprint and signature
  const fingerprint = await generateFingerprint();
  const signature = await generateRequestSignature(options.body || {});
  
  // Get security token
  let secToken = getSecurityToken();
  if (!secToken) {
    secToken = generateCSRFToken();
    setSecurityToken(secToken);
  }
  
  // Add security headers
  const secureHeaders = {
    ...options.headers,
    'X-Client-Fingerprint': JSON.stringify(fingerprint),
    'X-Request-Signature': signature.signature,
    'X-Request-Timestamp': signature.timestamp.toString(),
    'X-Request-Nonce': signature.nonce,
    'X-Security-Token': secToken,
    'X-Bot-Check': botCheck.score.toString(),
    'Content-Type': 'application/json'
  };
  
  return fetch(url, {
    ...options,
    headers: secureHeaders
  });
};

// Add random delays to mimic human behavior
export const humanDelay = (min = 100, max = 500) => {
  return new Promise(resolve => 
    setTimeout(resolve, Math.random() * (max - min) + min)
  );
};

// Verify user interaction before submission
export const verifyUserInteraction = () => {
  const interactions = {
    mouseMovements: window.mouseMovementCount || 0,
    keyPresses: window.keyPressCount || 0,
    clicks: window.clickCount || 0,
    timeOnPage: performance.now()
  };
  
  // Basic checks
  return (
    interactions.mouseMovements > 3 ||
    interactions.keyPresses > 2 ||
    interactions.clicks > 0
  ) && interactions.timeOnPage > 2000; // At least 2 seconds on page
};

// Track user interactions
if (typeof window !== 'undefined') {
  window.mouseMovementCount = 0;
  window.keyPressCount = 0;
  window.clickCount = 0;
  
  document.addEventListener('mousemove', () => {
    window.mouseMovementCount++;
  });
  
  document.addEventListener('keypress', () => {
    window.keyPressCount++;
  });
  
  document.addEventListener('click', () => {
    window.clickCount++;
  });
}

